<div *ngIf="errorMessage" class="bg-red-50 text-red-700 border border-red-200 rounded-md px-4 py-3 mb-4 text-sm">
  <i class="fas fa-exclamation-circle mr-1"></i> {{ errorMessage }}
</div>

<div *ngIf="loading" class="text-center text-gray-500 py-10">
  <i class="fas fa-spinner fa-spin mr-1"></i> Loading funnel...
</div>

<div *ngIf="!loading && report">
  <!-- Headline tiles -->
  <div class="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-4 mb-6">
    <div class="bg-white rounded-lg border border-gray-200 p-4">
      <div class="text-xs font-semibold text-gray-500 uppercase mb-1">Leads</div>
      <div class="text-2xl font-bold text-gray-900">{{ report.totals.leads | number }}</div>
    </div>
    <div class="bg-white rounded-lg border border-gray-200 p-4">
      <div class="text-xs font-semibold text-gray-500 uppercase mb-1">Sign-ups</div>
      <div class="text-2xl font-bold text-gray-900">{{ report.totals.signUps | number }}</div>
      <div class="text-xs text-gray-400" *ngIf="report.totals.signUpRate != null">{{ report.totals.signUpRate }}% of leads first seen in this period</div>
    </div>
    <div class="bg-white rounded-lg border border-gray-200 p-4">
      <div class="text-xs font-semibold text-gray-500 uppercase mb-1">First pickups</div>
      <div class="text-2xl font-bold text-gray-900">{{ report.totals.firstPickups | number }}</div>
      <div class="text-xs text-gray-400">{{ report.totals.pickups | number }} pickups in total</div>
    </div>
    <div class="bg-white rounded-lg border border-gray-200 p-4">
      <div class="text-xs font-semibold text-gray-500 uppercase mb-1">Revenue</div>
      <div class="text-2xl font-bold text-green-700">{{ report.totals.revenue | currency:'INR':'symbol':'1.0-0' }}</div>
      <div class="text-xs text-gray-400">GMV {{ report.totals.gmv | currency:'INR':'symbol':'1.0-0' }}</div>
    </div>
    <div class="bg-white rounded-lg border border-gray-200 p-4">
      <div class="text-xs font-semibold text-gray-500 uppercase mb-1">Meta spend</div>
      <div class="text-2xl font-bold text-gray-900">{{ report.totals.spend | currency:'INR':'symbol':'1.0-0' }}</div>
    </div>
    <div class="bg-white rounded-lg border border-gray-200 p-4">
      <div class="text-xs font-semibold text-gray-500 uppercase mb-1">CAC</div>
      <div class="text-2xl font-bold text-gray-900">{{ report.totals.cac != null ? (report.totals.cac | currency:'INR':'symbol':'1.0-0') : '—' }}</div>
      <div class="text-xs text-gray-400">per first pickup</div>
    </div>
    <div class="bg-white rounded-lg border border-gray-200 p-4">
      <div class="text-xs font-semibold text-gray-500 uppercase mb-1">Revenue ROAS</div>
      <div class="text-2xl font-bold text-gray-900">{{ report.totals.revenueRoas != null ? (report.totals.revenueRoas + '×') : '—' }}</div>
      <div class="text-xs text-gray-400" *ngIf="report.totals.gmvRoas != null">GMV ROAS {{ report.totals.gmvRoas }}×</div>
    </div>
  </div>

  <div *ngIf="report.rows.length === 0" class="bg-white rounded-lg border border-gray-200 p-6 text-center text-gray-500 text-sm">
    No leads, customers or Meta spend in this period.
  </div>

  <div *ngIf="report.rows.length > 0">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
      <div class="bg-white rounded-lg border border-gray-200 p-3">
        <app-chart-wrapper [data]="leadsChart" title="Leads by channel" type="bar" [height]="260"></app-chart-wrapper>
      </div>
      <div class="bg-white rounded-lg border border-gray-200 p-3">
        <app-chart-wrapper [data]="revenueChart" title="Revenue by channel (₹)" type="bar" [colors]="['#16a34a']" [height]="260"></app-chart-wrapper>
      </div>
    </div>

    <!-- Funnel table -->
    <div class="bg-white rounded-lg border border-gray-200 mb-4 overflow-x-auto">
      <table class="min-w-full text-sm">
        <thead class="bg-gray-50 text-xs text-gray-500 uppercase">
          <tr>
            <th class="text-left px-3 py-2">Channel</th>
            <th class="text-right px-3 py-2">Leads</th>
            <th class="text-right px-3 py-2" title="Leads first seen in this period who created an account within the attribution window. Not the same as Retention's new customers, which counts accounts created in this period.">Sign-ups</th>
            <th class="text-right px-3 py-2" title="Signed up, never booked, and checked a location we don't serve (% of sign-ups)">Out of area</th>
            <th class="text-right px-3 py-2" title="Leads who booked in the window, including existing customers who didn't sign up in it">Booked</th>
            <th class="text-right px-3 py-2" title="Sign-ups who booked ÷ (sign-ups − out of area)">Serviceable booking rate</th>
            <th class="text-right px-3 py-2">Picked up</th>
            <th class="text-right px-3 py-2">First pickups</th>
            <th class="text-right px-3 py-2">Revenue</th>
            <th class="text-right px-3 py-2">Spend</th>
            <th class="text-right px-3 py-2">Cost / lead</th>
            <th class="text-right px-3 py-2">Cost / sign-up</th>
            <th class="text-right px-3 py-2">CAC</th>
            <th class="text-right px-3 py-2">Revenue ROAS</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of report.rows" class="border-t border-gray-100">
            <td class="px-3 py-2 text-gray-900 whitespace-nowrap">{{ row.label }}</td>
            <td class="px-3 py-2 text-right">{{ row.leads | number }}</td>
            <td class="px-3 py-2 text-right">{{ row.signUps | number }}<span class="text-xs text-gray-400" *ngIf="row.signUpRate != null"> ({{ row.signUpRate }}%)</span></td>
            <td class="px-3 py-2 text-right">{{ row.outOfAreaSignUps | number }}<span class="text-xs text-gray-400" *ngIf="row.outOfAreaRate != null"> ({{ row.outOfAreaRate }}%)</span></td>
            <td class="px-3 py-2 text-right">{{ row.bookedCustomers | number }}<span class="text-xs text-gray-400" *ngIf="row.bookingRate != null"> ({{ row.bookingRate }}%)</span></td>
            <td class="px-3 py-2 text-right">{{ row.serviceableBookingRate != null ? row.serviceableBookingRate + '%' : '—' }}</td>
            <td class="px-3 py-2 text-right">{{ row.customersWithPickup | number }}<span class="text-xs text-gray-400" *ngIf="row.pickupRate != null"> ({{ row.pickupRate }}%)</span></td>
            <td class="px-3 py-2 text-right">{{ row.firstPickups | number }}</td>
            <td class="px-3 py-2 text-right text-green-700">{{ row.revenue | currency:'INR':'symbol':'1.0-0' }}</td>
            <td class="px-3 py-2 text-right">{{ row.spend ? (row.spend | currency:'INR':'symbol':'1.0-0') : '—' }}</td>
            <td class="px-3 py-2 text-right">{{ row.costPerLead != null ? (row.costPerLead | currency:'INR':'symbol':'1.0-0') : '—' }}</td>
            <td class="px-3 py-2 text-right">{{ row.costPerSignUp != null ? (row.costPerSignUp | currency:'INR':'symbol':'1.0-0') : '—' }}</td>
            <td class="px-3 py-2 text-right font-semibold">{{ row.cac != null ? (row.cac | currency:'INR':'symbol':'1.0-0') : '—' }}</td>
            <td class="px-3 py-2 text-right font-semibold">{{ row.revenueRoas != null ? (row.revenueRoas + '×') : '—' }}</td>
          </tr>
          <tr class="border-t-2 border-gray-200 bg-gray-50 font-semibold">
            <td class="px-3 py-2">Total</td>
            <td class="px-3 py-2 text-right">{{ report.totals.leads | number }}</td>
            <td class="px-3 py-2 text-right">{{ report.totals.signUps | number }}</td>
            <td class="px-3 py-2 text-right">{{ report.totals.outOfAreaSignUps | number }}<span class="text-xs text-gray-400" *ngIf="report.totals.outOfAreaRate != null"> ({{ report.totals.outOfAreaRate }}%)</span></td>
            <td class="px-3 py-2 text-right">{{ report.totals.bookedCustomers | number }}</td>
            <td class="px-3 py-2 text-right">{{ report.totals.serviceableBookingRate != null ? report.totals.serviceableBookingRate + '%' : '—' }}</td>
            <td class="px-3 py-2 text-right">{{ report.totals.customersWithPickup | number }}</td>
            <td class="px-3 py-2 text-right">{{ report.totals.firstPickups | number }}</td>
            <td class="px-3 py-2 text-right text-green-700">{{ report.totals.revenue | currency:'INR':'symbol':'1.0-0' }}</td>
            <td class="px-3 py-2 text-right">{{ report.totals.spend | currency:'INR':'symbol':'1.0-0' }}</td>
            <td class="px-3 py-2 text-right">{{ report.totals.costPerLead != null ? (report.totals.costPerLead | currency:'INR':'symbol':'1.0-0') : '—' }}</td>
            <td class="px-3 py-2 text-right">{{ report.totals.costPerSignUp != null ? (report.totals.costPerSignUp | currency:'INR':'symbol':'1.0-0') : '—' }}</td>
            <td class="px-3 py-2 text-right">{{ report.totals.cac != null ? (report.totals.cac | currency:'INR':'symbol':'1.0-0') : '—' }}</td>
            <td class="px-3 py-2 text-right">{{ report.totals.revenueRoas != null ? (report.totals.revenueRoas + '×') : '—' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <p class="text-xs text-gray-400 leading-relaxed">
    Leads are grouped by the date of their first touch (web landing or first WhatsApp message). Their sign-ups, bookings and
    pickups count if they happened within {{ report.attributionWindowDays }} days of it. Meta spend is assigned by each ad's
    destination (WhatsApp or website). "Out of area" counts sign-ups who never booked and checked a location we don't serve; the
    serviceable booking rate leaves them out (sign-ups who booked ÷ the remaining sign-ups). Booked also includes existing
    customers who didn't sign up in the window, so it can be higher than sign-ups.
    <ng-container *ngIf="report.unallocatedSpend > 0">
      {{ report.unallocatedSpend | currency:'INR':'symbol':'1.0-0' }} of Meta spend went to ads with no known destination and no leads,
      so it appears only in the total.
    </ng-container>
    Organic, search and offline channels show no spend: manual campaign costs (influencer fees, print) are not tracked yet.
  </p>
</div>
